import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { capitalizeFirstLetter } from '../../../../common/Utils/capitalizeFirstLetter';
import { useLanguageTranslations } from '../../../../common/Widget/SmartChoiceWidget/utils';
import { getLanguage } from '../../../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  getEthnicityDesc,
  getRaceDesc,
} from '../../../../utils/providerDetails.utils';
import {
  ATTRIBUTE_STATUS_TEXT,
  CHECKBOX_ITEM_NAMES,
  NULL_ATTRIBUTE_STATE,
} from '../MapView/CompareDrawer/utility/compareDrawerConstants';
import { getStatusText } from '../MapView/ShareDrawer/utils';
import { DividerStyle, LayoutGroupStyled } from './CompareSmartChoice.styled';
import { isAttributeChecked } from './utils';

export const PersonalFit = ({
  selectedAttributes,
  preferencesData,
  isSmartChoice,
  filteredProviders,
}) => {
  const { t } = useTranslation();
  const lagoonData = useLagoon('ui-messaging')();
  const language = getLanguage()?.code || 'en';
  const languageMap = useLanguageTranslations(language);

  const isProviderEthnicityEmpty = (provider) =>
    isEmpty(provider?.providerEthnicityExpress);
  const ethnicity_not_available = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_ETHINICITY_INFO_NOT_AVAILABLE,
  })?.message;
  const providerEthnicityText = (provider) =>
    isProviderEthnicityEmpty(provider)
      ? ethnicity_not_available
      : provider?.providerEthnicityExpress;
  const eapAcceptedText = (provider) =>
    t(
      getStatusText(
        provider?.isEap,
        ATTRIBUTE_STATUS_TEXT.EAP_ACCEPTED.TRUE,
        ATTRIBUTE_STATUS_TEXT.EAP_ACCEPTED.FALSE,
        NULL_ATTRIBUTE_STATE.EAP_ACCEPTED
      )
    );
  const isAttributeEmpty = (provider) => isEmpty(provider?.practiceLimitations);
  const practiceLimitationsText = (provider) => {
    if (isAttributeEmpty(provider))
      return t(NULL_ATTRIBUTE_STATE.PRACTICE_LIMITATIONS);
    if (provider?.practiceLimitations?.startAge !== '0')
      return t(ATTRIBUTE_STATUS_TEXT.PRACTICE_LIMITATIONS.TRUE);
    return t(ATTRIBUTE_STATUS_TEXT.PRACTICE_LIMITATIONS.FALSE);
  };

  const isServicesOfferedEmpty = (provider) =>
    isEmpty(provider?.servicesOffered);
  function formatAndTranslateScreamingSnakeCase(value: string) {
    return t(
      capitalizeFirstLetter(value.replaceAll('_', ' ').toLocaleLowerCase())
    );
  }
  const servicesOfferedText = (provider) =>
    isServicesOfferedEmpty(provider)
      ? t(NULL_ATTRIBUTE_STATE.SERVICES_OFFERED)
      : provider?.servicesOffered
          .map((value) => formatAndTranslateScreamingSnakeCase(value))
          .join(', ');
  const providerLanguages = (provider) => provider?.languagesSpoken ?? [];
  const providerLanguagesByOffice = (provider) =>
    provider?.languagesSpokenByOffice ?? [];

  const allProviderLanguages = (provider) => {
    const combinedLanguages = [
      ...providerLanguages(provider),
      ...providerLanguagesByOffice(provider).map((item) => item?.toLowerCase()),
    ];

    const translatedLanguages = combinedLanguages
      .map((lang) => languageMap[lang])
      .filter((item) => item);

    return [...new Set(translatedLanguages)];
  };

  const preferredLanguage = preferencesData?.languagesSpoken ?? '';
  const preferredGender = preferencesData?.providerGender ?? '';
  const preferredBoardCert = preferencesData?.providerBoardCertStatus ?? '';

  const spoken_languages_unknown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_LANGUAGE_UNKNOWN,
  })?.message;
  const identified_as_male = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_MALE,
  })?.message;
  const identified_as_female = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_FEMALE,
  })?.message;
  const not_certified = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_BOARD_CERTIFIED,
  })?.message;
  const rating_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_CONSUMER_RATING_INFO_UNAVAILABLE,
  })?.message;
  const provider_gender_unknown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_GENDER_UNKNOWN,
  })?.message;
  const provider_board_certified = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_BOARD_CERTIFIED_IN,
  })?.message;

  return (
    <React.Fragment>
      {selectedAttributes?.find(
        (item) =>
          item?.name === 'languageSpoken' || item?.name === 'languagesSpoken'
      )?.isChecked &&
        ((isSmartChoice && preferredLanguage !== '') || !isSmartChoice) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) =>
                provider?.recommendationDetails?.personalFit?.languageSpoken ===
                'unknown' ? (
                  spoken_languages_unknown
                ) : (
                  <>
                    <LayoutGroupStyled
                      css={{ minHeight: '50px' }}
                      data-testid="provider-languages"
                      key={index}
                    >
                      <Text>
                        {allProviderLanguages(provider)?.join(', ') ||
                          spoken_languages_unknown}
                      </Text>
                    </LayoutGroupStyled>
                  </>
                )
              )}
            </LayoutGroupStyled>

            {isAttributeChecked(selectedAttributes, [
              'gender',
              'boardCertified',
              'reviews',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'servicesOffered',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'gender')?.isChecked &&
        ((isSmartChoice && preferredGender !== '') || !isSmartChoice) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) =>
                !provider?.gender ||
                provider?.gender === '' ||
                provider?.gender?.toLowerCase() === 'unknown' ? (
                  provider_gender_unknown
                ) : (
                  <>
                    <LayoutGroupStyled
                      data-testid="provider-gender"
                      key={index}
                    >
                      <Text css={{ textTransform: 'capitalize' }}>
                        {provider?.gender === 'male' && identified_as_male}
                        {provider?.gender === 'female' && identified_as_female}
                        {provider?.gender !== 'male' &&
                          provider?.gender !== 'female' &&
                          provider?.gender}
                      </Text>
                    </LayoutGroupStyled>
                  </>
                )
              )}
            </LayoutGroupStyled>

            {isSmartChoice &&
              isAttributeChecked(selectedAttributes, [
                'boardCertified',
                'reviews',
              ]) && (
                <LayoutGroupStyled
                  css={{ '& > div': { width: '100%' } }}
                  cssProps={{ minHeightAuto: true }}
                  grow
                >
                  {filteredProviders?.map((_, index) => (
                    <React.Fragment key={index}>
                      <DividerStyle />
                    </React.Fragment>
                  ))}
                </LayoutGroupStyled>
              )}
            {!isSmartChoice &&
              isAttributeChecked(selectedAttributes, [
                'boardCertified',
                'reviews',
                'providerEthnicityExpress',
                'eapAccepted',
                'practiceLimitations',
                'servicesOffered',
              ]) && (
                <LayoutGroupStyled
                  css={{ '& > div': { width: '100%' } }}
                  cssProps={{ minHeightAuto: true }}
                  grow
                >
                  {filteredProviders?.map((_, index) => (
                    <React.Fragment key={index}>
                      <DividerStyle />
                    </React.Fragment>
                  ))}
                </LayoutGroupStyled>
              )}
          </>
        )}
      {selectedAttributes.find(
        (item) => item.name === CHECKBOX_ITEM_NAMES.PROVIDER_RACE
      )?.isChecked && (
        <>
          <LayoutGroupStyled cssProps={{ fullWidthDivs: true }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled data-testid="provider-race" key={index}>
                <Text>
                  {getRaceDesc(
                    provider.providerRace,
                    t,
                    'COMPARE.RACE_NOT_AVAILABLE'
                  )}
                </Text>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          {isAttributeChecked(selectedAttributes, [
            'eapAccepted',
            'practiceLimitations',
            'servicesOffered',
            'boardCertified',
            'reviews',
            'providerEthnicityExpress',
            'ethnicity',
          ]) && (
            <LayoutGroupStyled
              cssProps={{ minHeightAuto: true, fullWidthDivs: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {selectedAttributes.find(
        (item) => item.name === CHECKBOX_ITEM_NAMES.ETHNICITY
      )?.isChecked && (
        <>
          <LayoutGroupStyled cssProps={{ fullWidthDivs: true }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled data-testid="provider-ethnicity" key={index}>
                <Text>
                  {getEthnicityDesc(provider?.providerEthnicity, t, true)}
                </Text>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          {isAttributeChecked(selectedAttributes, [
            'eapAccepted',
            'practiceLimitations',
            'servicesOffered',
            'boardCertified',
            'reviews',
            'providerEthnicityExpress',
          ]) && (
            <LayoutGroupStyled
              cssProps={{ minHeightAuto: true, fullWidthDivs: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {selectedAttributes?.find((item) => item?.name === 'boardCertified')
        ?.isChecked &&
        preferredBoardCert === 'y' && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  data-testid="provider-board-certifications"
                  key={index}
                >
                  <Text css={{ textTransform: 'capitalize' }}>
                    {provider?.boardCertifications?.length
                      ? provider_board_certified?.replace(
                          '{{providerBoardCertifications}}',
                          provider?.boardCertifications
                            ?.map((cert) => cert?.value?.toLowerCase())
                            ?.join(', ')
                        )
                      : not_certified}
                  </Text>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            {isAttributeChecked(selectedAttributes, [
              'reviews',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'servicesOffered',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'reviews')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled data-testid="provider-ratings" key={index}>
                {provider?.healthGradeRating ? (
                  <Flex alignItems="center">
                    <IconSymbol color="#F5B700" icon="star" outlined />
                    <Text>{`${provider?.healthGradeRating} (${provider?.surveyCount})`}</Text>
                  </Flex>
                ) : (
                  <Text>{rating_unavailable}</Text>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          {isAttributeChecked(selectedAttributes, [
            'providerEthnicityExpress',
            'eapAccepted',
            'practiceLimitations',
            'servicesOffered',
          ]) && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {/* Provider Ethnicity */}
      {selectedAttributes.find(
        (item) => item.name === 'providerEthnicityExpress'
      )?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                data-testid="provider-ethnicity-express"
                key={index}
              >
                <Text>{providerEthnicityText(provider)}</Text>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          {isAttributeChecked(selectedAttributes, [
            'eapAccepted',
            'practiceLimitations',
            'servicesOffered',
          ]) && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}

      {/* EAP ACCEPTED */}
      {selectedAttributes.find((item) => item.name === 'eapAccepted')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                data-testid="provider-eap-accepted"
                key={index}
              >
                <Text>{eapAcceptedText(provider)}</Text>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          {isAttributeChecked(selectedAttributes, [
            'practiceLimitations',
            'servicesOffered',
          ]) && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {/* Practice Limitations */}
      {selectedAttributes.find((item) => item.name === 'practiceLimitations')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                data-testid="provider-practice-limitations"
                key={index}
              >
                {provider?.practiceLimitations?.startAge ? (
                  <>
                    <IconMaterial
                      color="$primary1"
                      icon="back_hand"
                      size={20}
                    />
                    <Text css={{ marginLeft: '8px' }}>
                      {provider?.practiceLimitations?.startAge === '0'
                        ? practiceLimitationsText(provider)
                        : `${practiceLimitationsText(provider)}: ${
                            provider?.practiceLimitations?.startAge
                          }+`}
                    </Text>
                  </>
                ) : (
                  <Text>{practiceLimitationsText(provider)}</Text>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          {isAttributeChecked(selectedAttributes, ['servicesOffered']) && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}

      {/* Serives Offered */}
      {selectedAttributes.find((item) => item.name === 'servicesOffered')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((_, index) => (
              <LayoutGroupStyled
                data-testid="provider-services-offered"
                key={index}
              >
                <Text> {t(servicesOfferedText(providerEthnicityText))}</Text>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
        </>
      )}
    </React.Fragment>
  );
};
